/**
 * UI step 3 (D442): the offers and bonuses on the map as strips, and how they stack. At most
 * two strips show on the map: the goal strip first, then the most urgent offer; the rest wait
 * behind one "N more offers" button that opens the Offers and bonuses sheet, in the same order.
 * Pure: the screen draws what this says.
 */
import { idleBonusOffers } from '@hedgerow/sim';
import type { IdleWorldState } from '@hedgerow/sim';
import type { IdleContent } from '@hedgerow/data';
import { coins } from './notation.ts';
import { t as term } from './terms.ts';
import { activeBonuses, fmtSpan, incomeWhat, marketOfferText, visitorText } from './view.ts';

export type OfferKind = 'market_offer' | 'visitor' | 'market_active' | 'weather_active';

export interface OfferStrip {
  /** The id a claim is made with, or the bonus's own name when nothing is claimed. */
  id: string;
  kind: OfferKind;
  /** The test id the strip carries on the map. */
  testId: string;
  icon: string;
  /** The sentence, 13px. */
  text: string;
  /** The timer or detail under it, 11px mono. */
  detail: string;
  /** When it goes, so strips sort by what runs out first. */
  until: number;
  /** Something to collect now: the strip takes the coin fill. */
  ready: boolean;
  /** The 44px button on the right; none when the bonus is simply in force. */
  action: 'Claim' | 'Collect' | null;
}

/** The most strips on the map, the goal strip included. */
export const MAP_STRIPS = 2;

/** Ready to collect first, then the shortest timer; a tie keeps the order they were made in. */
export function sortStrips(strips: OfferStrip[]): OfferStrip[] {
  return strips
    .map((s, i) => ({ s, i }))
    .sort((a, b) => Number(b.s.ready) - Number(a.s.ready) || a.s.until - b.s.until || a.i - b.i)
    .map((x) => x.s);
}

/** Every offer and bonus in force at `t`, most urgent first. */
export function offerStrips(world: IdleWorldState, content: IdleContent, t: number): OfferStrip[] {
  const { market, weather } = activeBonuses(world, t);
  const offers = idleBonusOffers(world, content, t);
  const out: OfferStrip[] = [];
  const offer = offers.find((o) => o.kind === 'market_day');
  if (offer && !market)
    out.push({
      id: offer.id,
      kind: 'market_offer',
      testId: 'market-offer',
      icon: 'store',
      text: marketOfferText(offer.factor ?? 2, offer.durationMs ?? 0),
      detail: `Ends in ${fmtSpan(offer.until - t)}`,
      until: offer.until,
      ready: false,
      action: 'Claim',
    });
  const visitor = offers.find((o) => o.kind === 'visitor');
  if (visitor)
    out.push({
      id: visitor.id,
      kind: 'visitor',
      testId: 'visitor-offer',
      icon: 'user',
      text: visitorText(content, visitor.visitor ?? ''),
      detail: `${coins(visitor.coins ?? 0)}, leaves in ${fmtSpan(visitor.until - t)}`,
      until: visitor.until,
      ready: true,
      action: 'Collect',
    });
  if (market)
    out.push({
      id: 'market_active',
      kind: 'market_active',
      testId: 'market-active',
      icon: 'store',
      text: `${term('market')} is on`,
      detail: `${incomeWhat(market.factor)}, ${fmtSpan(market.until - t)} left`,
      until: market.until,
      ready: false,
      action: null,
    });
  if (weather)
    out.push({
      id: 'weather_active',
      kind: 'weather_active',
      testId: 'weather-active',
      icon: 'sun',
      text: `${term('weather')} is on`,
      detail: `${Math.round((weather.factor - 1) * 100)}% more income, ${fmtSpan(weather.until - t)} left`,
      until: weather.until,
      ready: false,
      action: null,
    });
  return sortStrips(out);
}

/**
 * Which offers show on the map beside the goal strip, and which wait behind the button.
 * With no goal strip the map has room for two offers.
 */
export function stackStrips(
  strips: OfferStrip[],
  hasGoal = true,
): { shown: OfferStrip[]; more: OfferStrip[] } {
  const room = MAP_STRIPS - (hasGoal ? 1 : 0);
  return { shown: strips.slice(0, room), more: strips.slice(room) };
}

/** `1 more offer`, `2 more offers`. */
export function moreOffersText(n: number): string {
  return `${n} more offer${n === 1 ? '' : 's'}`;
}
